import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import type { OptionItem } from '@/components/ui/data-table-customized'
import type { CustomerContract } from '../types'
import {
    CONTRACT_STATUS,
    CONTRACT_SLA_OPTIONS,
    CONTRACT_NDA_OPTIONS,
    CONTRACT_RENEW_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
    CONTRACT_DIRECTION_OPTIONS,
} from '../constants'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { generatePathDatacentral } from '@/utils/path'

export const getContractColumns = (
    t: TFunction
): DataTableColumn<CustomerContract>[] => [
    {
        key: 'contract_id',
        title: t('contract_id'),
        width: 100,
        sortable: true,
        formatter: createInternalLinkFormatter({
            pathKey: 'contractDetail',
            pathParams: (value) => ({ id: value }),
        }),
    },
    {
        key: 'contract_number_id',
        title: t('contract_number_id'),
        width: 200,
        sortable: false,
        formatter: createInternalLinkFormatter({
            pathKey: 'contractDetail',
            pathParams: (_, row) => ({ id: String(row.contract_id) }),
            label: (_, row) => row.contract_number_id,
        }),
        pinned: 'left',
    },
    {
        key: 'contract_key',
        title: t('contract_key'),
        width: 200,
    },
    {
        key: 'service_id' as any,
        title: t('contract_service'),
        width: 350,
        sortable: false,
        render: (_, row) => {
            if (!row.service_id) return '-'
            return (
                <Link
                    to={generatePathDatacentral('/services/:id', {
                        id: row.service_id,
                    })}
                    className="text-primary hover:underline uppercase text-xs"
                >
                    {row.product_group_name} - {row.service_name}
                </Link>
            )
        },
    },
    {
        key: 'contract_status',
        title: t('contract_status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: CONTRACT_STATUS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_role',
        title: t('contract_role'),
        width: 150,
        formatter: createOptionsFormatter({
            options: CONTRACT_ROLE_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_direction',
        title: t('contract_direction'),
        width: 140,
        formatter: createOptionsFormatter({
            options: CONTRACT_DIRECTION_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_value',
        title: t('contract_value'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: 'VND' }),
    },
    {
        key: 'contract_effective_date',
        title: t('contract_effective_date'),
        width: 140,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'contract_expiry_date',
        title: t('contract_expiry_date'),
        width: 140,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'sla_enable',
        title: t('sla_enable'),
        width: 120,
        formatter: createOptionsFormatter({
            options: CONTRACT_SLA_OPTIONS(t).map((o) => ({
                ...o,
                value: o.value as any,
            })) as OptionItem[],
        }),
    },
    {
        key: 'nda_enable',
        title: t('nda_enable'),
        width: 120,
        formatter: createOptionsFormatter({
            options: CONTRACT_NDA_OPTIONS(t).map((o) => ({
                ...o,
                value: o.value as any,
            })) as OptionItem[],
        }),
    },
    {
        key: 'auto_renewable',
        title: t('auto_renewable'),
        width: 150,
        formatter: createOptionsFormatter({
            options: CONTRACT_RENEW_OPTIONS(t).map((o) => ({
                ...o,
                value: o.value as any,
            })) as OptionItem[],
        }),
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 120,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

export const getContractFilterItems = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'contract_status',
        type: 'multi-select',
        label: t('contract_status'),
        placeholder: t('select-status'),
        options: CONTRACT_STATUS(t).map((s) => ({
            value: s.value,
            label: s.label,
        })),
    },
    {
        name: 'contract_role',
        type: 'multi-select',
        label: t('contract_role'),
        placeholder: t('contract_role'),
        options: CONTRACT_ROLE_OPTIONS(t).map((s) => ({
            value: s.value,
            label: s.label,
        })),
    },
    {
        name: 'contract_direction',
        type: 'multi-select',
        label: t('contract_direction'),
        placeholder: t('contract_direction'),
        options: CONTRACT_DIRECTION_OPTIONS(t).map((s) => ({
            value: s.value,
            label: s.label,
        })),
    },
    {
        name: 'contract_effective_date',
        type: 'daterangepicker',
        label: t('contract_effective_date'),
        names: ['contract_effective_date', 'contract_effective_date_to'],
        showPresets: true,
    },
    {
        name: 'contract_expiry_date',
        type: 'daterangepicker',
        label: t('contract_expiry_date'),
        names: ['contract_expiry_date', 'contract_expiry_date_to'],
        showPresets: true,
    },
    {
        name: 'sla_enable',
        type: 'multi-select',
        label: t('sla_enable'),
        placeholder: t('select-sla'),
        options: CONTRACT_SLA_OPTIONS(t).map((o) => ({
            value: String(o.value),
            label: o.label,
        })),
    },
    {
        name: 'nda_enable',
        type: 'multi-select',
        label: t('nda_enable'),
        placeholder: t('select-nda'),
        options: CONTRACT_NDA_OPTIONS(t).map((o) => ({
            value: String(o.value),
            label: o.label,
        })),
    },
    {
        name: 'auto_renewable',
        type: 'multi-select',
        label: t('auto_renewable'),
        placeholder: t('select-auto-renewable'),
        options: CONTRACT_RENEW_OPTIONS(t).map((o) => ({
            value: String(o.value),
            label: o.label,
        })),
    },
    {
        name: 'created_at',
        type: 'daterangepicker',
        label: t('created_at'),
        names: ['created_at', 'created_at_to'],
        showPresets: true,
    },
]
